import axios from "axios";
import type {
  AccountingPeriod,
  AccountMappingView,
  CreatedBatch,
  CreateJournalEntryPayload,
  ExportPreview,
  JournalEntryDetail,
  JournalEntryListQuery,
  JournalEntryListResponse,
  NeedsReviewEntry,
  PeriodEvent,
  PostableAccount,
  PostingBatch,
  RelatedJournalEntry,
  RetryResult,
} from "@/interfaces/accounting";
import { apiClient } from "@/lib/apiClient";

/** Drops empty params so an unset date filter isn't sent as "" (which the API would reject). */
function cleanQuery(q: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

/** The subledger's journal entries — server-paginated, filtered on the accounting date. GFG-only. */
export async function fetchJournalEntries(
  query: JournalEntryListQuery = {},
): Promise<JournalEntryListResponse> {
  const { data } = await apiClient.get<{ success: boolean } & JournalEntryListResponse>(
    "/admin/accounting/journal-entries",
    { params: cleanQuery(query) },
  );
  return data;
}

/** One entry with its lines and resolved source — the show page. */
export async function fetchJournalEntry(id: number): Promise<JournalEntryDetail> {
  const { data } = await apiClient.get<{ success: boolean; data: JournalEntryDetail }>(
    `/admin/accounting/journal-entries/${id}`,
  );
  return data.data;
}

/** The account picker for a hand-keyed entry — sorted by QuickBooks number. */
export async function fetchPostableAccounts(): Promise<PostableAccount[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PostableAccount[] }>(
    "/admin/accounting/accounts",
  );
  return data.data;
}

/** The Chart of Account Mapper — every subledger key with its QuickBooks binding. Read-only. */
export async function fetchCoaMapping(): Promise<AccountMappingView> {
  const { data } = await apiClient.get<{ success: boolean; data: AccountMappingView }>(
    "/admin/accounting/coa-mapping",
  );
  return data.data;
}

/** Posts a journal entry keyed by hand. Rethrows the server's message so the screen can show it. */
export async function createJournalEntry(payload: CreateJournalEntryPayload): Promise<void> {
  try {
    await apiClient.post("/admin/accounting/journal-entries", payload);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const msg = (error.response?.data as { error?: string } | undefined)?.error;
      throw new Error(msg || "Couldn't post the journal entry.");
    }
    throw error instanceof Error ? error : new Error("Couldn't post the journal entry.");
  }
}

/** Every accounting period and its state. GFG-only. */
export async function fetchPeriods(range?: {
  from?: string;
  to?: string;
}): Promise<AccountingPeriod[]> {
  const { data } = await apiClient.get<{ success: boolean; data: AccountingPeriod[] }>(
    "/admin/accounting/periods",
    { params: cleanQuery(range ?? {}) },
  );
  return data.data;
}

/** What would go out if a batch were created now, and what would be left behind. */
export async function fetchExportPreview(period: string): Promise<ExportPreview> {
  const { data } = await apiClient.get<{ success: boolean; data: ExportPreview }>(
    `/admin/accounting/periods/${period}/export-preview`,
  );
  return data.data;
}

/** Posts a batch: claims the period's unbatched entries and returns the QuickBooks file. */
export async function createBatch(period: string): Promise<CreatedBatch> {
  const { data } = await apiClient.post<{ success: boolean; data: CreatedBatch }>(
    `/admin/accounting/periods/${period}/batches`,
  );
  return data.data;
}

/** Batches, optionally narrowed to one period. */
export async function fetchBatches(period?: string): Promise<PostingBatch[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PostingBatch[] }>(
    "/admin/accounting/batches",
    { params: cleanQuery({ period }) },
  );
  return data.data;
}

/** Re-renders a batch's file from stored membership — safe to call any number of times. */
export async function fetchBatchCsv(batchId: number): Promise<string> {
  const { data } = await apiClient.get<string>(`/admin/accounting/batches/${batchId}/csv`, {
    responseType: "text",
  });
  return data;
}

export async function closePeriod(period: string): Promise<void> {
  await apiClient.post(`/admin/accounting/periods/${period}/close`);
}

export async function reopenPeriod(period: string): Promise<void> {
  await apiClient.post(`/admin/accounting/periods/${period}/reopen`);
}

/** Entries held for review — their own surface, because one nobody looks at shortens a month. */
export async function fetchNeedsReview(): Promise<NeedsReviewEntry[]> {
  const { data } = await apiClient.get<{ success: boolean; data: NeedsReviewEntry[] }>(
    "/admin/accounting/needs-review",
  );
  return data.data;
}

/** Re-runs account resolution after the mapping has been corrected. Not an edit. */
export async function retryEntry(id: number): Promise<RetryResult> {
  const { data } = await apiClient.post<{ success: boolean; data: RetryResult }>(
    `/admin/accounting/journal-entries/${id}/retry`,
  );
  return data.data;
}

/** A period's activity feed — who posted, who signed off, who reopened, and when. */
export async function fetchPeriodEvents(period: string): Promise<PeriodEvent[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PeriodEvent[] }>(
    `/admin/accounting/periods/${period}/events`,
  );
  return data.data;
}

/** Every journal entry for one invoice or purchase, including receipt/refund/lot-sourced ones. */
export async function fetchRelatedJournalEntries(
  sourceType: number,
  sourceId: number,
): Promise<RelatedJournalEntry[]> {
  const { data } = await apiClient.get<{ success: boolean; data: RelatedJournalEntry[] }>(
    "/admin/accounting/related-entries",
    { params: { sourceType, sourceId } },
  );
  return data.data;
}
